/**
 * The site's URL shape, in one place.
 *
 * Danish is the default language and lives at the bare paths; English is the same
 * tree under `/en`. That split exists for search engines rather than for users:
 * as long as the language was a client-side toggle on a single URL, Google only
 * ever saw the Danish copy, and there was no honest way to declare an alternate.
 * With a URL per language we can emit `hreflang` and a per-language canonical,
 * and a shared link opens in the language it was shared in.
 *
 * The paths themselves stay Danish (`/udbyder/...`) — they are content aimed at a
 * Danish audience, and they are already indexed.
 *
 * Pure string handling, no DOM and no React, so the build-time prerenderer and
 * the browser can agree on every URL from the same source.
 */
export type Language = "da" | "en";

export const LANGUAGES: readonly Language[] = ["da", "en"];

/** The default language — the one that owns the bare paths and `x-default`. */
export const DEFAULT_LANGUAGE: Language = "da";

export type PageKind = "home" | "providerIndex" | "provider" | "admin";

export interface Route {
  language: Language;
  kind: PageKind;
  /** Provider slug, only on `kind === "provider"`. */
  slug?: string;
}

/** Trailing slashes are noise everywhere except at the root. */
function normalise(pathname: string): string {
  const p = pathname.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

export function parseRoute(pathname: string): Route {
  const path = normalise(pathname);

  // The admin backoffice is not translated and stays on one URL — it is
  // noindex anyway, so it has nothing to gain from a language tree.
  if (path === "/admin" || path.startsWith("/admin/")) {
    return { language: DEFAULT_LANGUAGE, kind: "admin" };
  }

  const isEnglish = path === "/en" || path.startsWith("/en/");
  const language: Language = isEnglish ? "en" : "da";
  const rest = isEnglish ? normalise(path.slice(3)) : path;

  const provider = /^\/udbyder\/([a-z0-9-]+)$/.exec(rest);
  if (provider) return { language, kind: "provider", slug: provider[1] };
  if (rest === "/udbyder") return { language, kind: "providerIndex" };
  return { language, kind: "home" };
}

/** The URL for a page in a given language. Danish keeps the bare path. */
export function buildPath(kind: PageKind, language: Language, slug?: string): string {
  if (kind === "admin") return "/admin";
  const prefix = language === DEFAULT_LANGUAGE ? "" : `/${language}`;
  switch (kind) {
    case "provider":
      return `${prefix}/udbyder/${slug}`;
    case "providerIndex":
      return `${prefix}/udbyder`;
    case "home":
      return prefix === "" ? "/" : `${prefix}/`;
  }
}

/** The same page in another language — what the language switcher links to. */
export const samePageIn = (route: Route, language: Language): string =>
  buildPath(route.kind, language, route.slug);
